import type { CofQuizSubMode, KeySlot } from '@tunasong/music-lib'
import { Button } from '@tunasong/ui-lib'
import { useState, type FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { CofQuizModePicker } from './cof-quiz-mode-picker.js'
import type { CofSubMode } from './cof-wheel-params.js'
import styles from './cof-wheel-welcome.module.css'

export interface CofWheelWelcomeProps {
  preSelectedSubMode: CofSubMode | null
  preSelectedKey: KeySlot | null
  onStartExplore: (key: { name: string; ring: 'major' | 'minor' }) => void
  onStartQuiz: (subMode: CofQuizSubMode) => void
}

/**
 * CoF Wheel v1.5 welcome surface — the Explore card plus the three quiz
 * sub-mode cards (Mixed / Relationships / Signatures) via `CofQuizModePicker`.
 *
 * Spec: docs/superpowers/specs/2026-05-28-cof-wheel-v1.5-design.md §6.5
 */
export const CofWheelWelcome: FC<CofWheelWelcomeProps> = props => {
  const { preSelectedSubMode, preSelectedKey, onStartExplore, onStartQuiz } = props
  const [activeSubMode] = useState<CofSubMode | null>(preSelectedSubMode)

  const toolCopy = useTheoryCopy('cof-wheel')
  const exploreCopy = useTheoryCopy('cof-explore')

  const handleExplore = (): void => {
    const key = preSelectedKey ? preSelectedKey.canonical : 'C'
    onStartExplore({ name: key, ring: 'major' })
  }

  return (
    <div className={styles.welcome}>
      <header className={styles.intro}>
        <h1 className={styles.title}>{toolCopy?.displayName ?? 'Circle of Fifths'}</h1>
        {toolCopy?.body ? <p className={styles.tagline}>{toolCopy.body}</p> : null}
      </header>

      <Button
        type="button"
        variant="outlined"
        onClick={handleExplore}
        aria-pressed={activeSubMode === 'explore'}
        className={styles.subModeCard}
      >
        <span className={styles.cardTitle}>{exploreCopy?.displayName ?? 'Wheel Explore'}</span>
        {exploreCopy?.body ? <span className={styles.cardBody}>{exploreCopy.body}</span> : null}
      </Button>

      <CofQuizModePicker onStart={onStartQuiz} />
    </div>
  )
}
